import React, { useState, useMemo } from 'react';
import { FileText, Camera, Check } from 'lucide-react';
import { Player, SelectionData, Team } from '../Model';
import { restClient } from '../RestClient';
import { DownloadHelper } from '../DownloadHelper';
import { LoadingButton } from '../LoadingButton';

async function captureTeams(teams: Team[], onError?: (message: string) => void): Promise<void> {
  try {
    const result = await restClient.captureTeams(teams);
    if (result.IsSuccess) {
      const blob = result.Value;

      // Create a fake response object for DownloadHelper compatibility
      const fakeResponse = {
        headers: {
          get: (name: string) => {
            if (name.toLowerCase() === 'content-disposition') {
              return `attachment; filename=teams_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.png`;
            }
            return null;
          }
        }
      };

      DownloadHelper.downloadBlob(blob, fakeResponse as any, 'teams.png');
    } else {
      console.error('Error capturing teams:', result.Error);
      onError?.(`${result.Error.code}: ${result.Error.description}`);
    }
  } catch (error) {
    console.error('Unexpected error capturing teams:', error);
    onError?.('Unexpected error occurred while capturing teams');
  }
}

// Helper to count detected players from text input
function countPlayersFromText(text: string): number {
  if (!text.trim()) return 0;
  const lines = text.split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => line.replace(/^\d+[.)\-\s]+/, '').trim()) // Remove numbering
    .filter(line => line.length > 0);
  return lines.length;
}

// Text Assignment Panel Component
function TextAssignmentPanel({
  textInput,
  setTextInput,
  isAssigningFromText,
  onSubmit,
  onHide
}: {
  textInput: string;
  setTextInput: (text: string) => void;
  isAssigningFromText: boolean;
  onSubmit: () => void;
  onHide: () => void;
}) {
  const playerCount = useMemo(() => countPlayersFromText(textInput), [textInput]);

  return (
    <div className="text-assignment-section">
      <div className="text-assignment-header">
        <h4 className="text-assignment-title">Add Players from Text</h4>
        {playerCount > 0 && (
          <span className="text-assignment-count">
            {playerCount} player{playerCount !== 1 ? 's' : ''} detected
          </span>
        )}
      </div>

      <p className="text-assignment-description">
        Paste a list of player names below (one name per line or numbered list).
      </p>

      <textarea
        value={textInput}
        onChange={(e) => setTextInput(e.target.value)}
        placeholder="John Smith&#10;Jane Doe&#10;Bob Wilson"
        className="text-assignment-textarea"
      />

      <div className="text-assignment-helper">
        <Check size={14} className="text-assignment-helper-icon" />
        <span>Unknown players will be created automatically</span>
      </div>

      <div className="text-assignment-actions">
        <LoadingButton
          isLoading={isAssigningFromText}
          onClick={onSubmit}
          disabled={!textInput.trim()}
          className={`btn ${!textInput.trim() ? 'btn-secondary' : 'btn-primary'}`}
          loadingText="Balancing..."
        >
          Balance Teams
        </LoadingButton>
        <button
          onClick={onHide}
          disabled={isAssigningFromText}
          className="btn btn-outline-neutral"
        >
          Hide
        </button>
      </div>
    </div>
  );
}

type TeamToolBarProps = {
  players: Player[];
  maxPlayers: number;
  setMaxPlayers: (value: number) => void;
  numberOfTeams: number;
  setNumberOfTeams: (value: number) => void;
  handleAssignTeams: () => void;
  handleAssignTeamsFromText: (text: string) => void;
  teams?: Team[];
  showCaptureButton?: boolean;
  selectionData?: SelectionData | null;
  onError?: (message: string) => void;
};

function TeamToolBar({
  players,
  maxPlayers,
  setMaxPlayers,
  numberOfTeams,
  setNumberOfTeams,
  handleAssignTeams,
  handleAssignTeamsFromText,
  teams,
  showCaptureButton,
  selectionData,
  onError
}: TeamToolBarProps) {
  const [showTextAssignment, setShowTextAssignment] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [isAssigningTeams, setIsAssigningTeams] = useState(false);
  const [isAssigningFromText, setIsAssigningFromText] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const handleTextSubmit = async () => {
    if (textInput.trim()) {
      setIsAssigningFromText(true);
      try {
        await handleAssignTeamsFromText(textInput);
      } finally {
        setIsAssigningFromText(false);
      }
    }
  };

  const handleAssignClick = async () => {
    setIsAssigningTeams(true);
    try {
      await handleAssignTeams();
    } finally {
      setIsAssigningTeams(false);
    }
  };

  const handleCaptureClick = async () => {
    if (teams && teams.length > 0) {
      setIsCapturing(true);
      try {
        // Get teams info from selectionData if available, otherwise use defaults
        const teamInfos = selectionData?.teams || [];

        // Create teams array with players assigned, merging with team info if available
        const teamsWithPlayers = teams.map((team, index) => {
          const teamInfo = teamInfos.find(info => info.id === team.id) ||
                          { id: team.id, version: team.version, name: team.name || `Team ${index + 1}`, color: team.color || '#007bff', selectionDataId: team.selectionDataId };
          return {
            ...teamInfo,
            players: team.players || []
          };
        });

        await captureTeams(teamsWithPlayers, onError);
      } finally {
        setIsCapturing(false);
      }
    }
  };

  return (
    <div className="team-toolbar">
      {/* Main toolbar */}
      <div className="team-toolbar-main">
        <label className="team-toolbar-label">
          Players:
          <input
            type="number"
            min={1}
            max={players.length}
            value={maxPlayers}
            onChange={e => setMaxPlayers(Number(e.target.value))}
            className="team-toolbar-input"
          />
        </label>
        <label className="team-toolbar-label">
          Teams:
          <input
            type="number"
            min={2}
            max={10}
            value={numberOfTeams}
            onChange={e => setNumberOfTeams(Number(e.target.value))}
            className="team-toolbar-input"
          />
        </label>
        <LoadingButton
          isLoading={isAssigningTeams}
          onClick={handleAssignClick}
          disabled={players.length < 2}
          className={`btn ${players.length < 2 ? 'btn-secondary' : 'btn-primary'}`}
          loadingText="Balancing..."
        >
          Balance Teams
        </LoadingButton>
        <button
          onClick={() => setShowTextAssignment(!showTextAssignment)}
          disabled={isAssigningTeams || isAssigningFromText}
          className={`btn btn-outline-neutral ${showTextAssignment ? 'btn-outline-neutral-active' : ''}`}
        >
          <FileText size={14} />
          {showTextAssignment ? 'Hide' : 'From Text'}
        </button>
        {showCaptureButton && teams && teams.length > 0 && (
          <LoadingButton
            isLoading={isCapturing}
            onClick={handleCaptureClick}
            className="btn btn-outline-neutral"
            loadingText="Capturing..."
          >
            <Camera size={14} />
            Capture
          </LoadingButton>
        )}
      </div>

      {/* Text assignment section */}
      {showTextAssignment && (
        <TextAssignmentPanel
          textInput={textInput}
          setTextInput={setTextInput}
          isAssigningFromText={isAssigningFromText}
          onSubmit={handleTextSubmit}
          onHide={() => setShowTextAssignment(false)}
        />
      )}
    </div>
  );
}

export default TeamToolBar;
